import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Loader2,
  CheckCircle2,
  Briefcase,
  Wallet,
  User,
  Calendar,
  DollarSign,
  FileText,
  Upload,
  FileCheck
} from 'lucide-react';
import styles from './ExpenseDrawer.module.css';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';

const ExpenseDrawer = ({ isOpen, onClose, onSuccess, expense }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const fileInputRef = useRef(null);

  const [dropdowns, setDropdowns] = useState({
    projects: [],
    categories: [],
    employees: []
  });

  const [formData, setFormData] = useState({
    project_id: '',
    category_id: '',
    expense_by: '',
    expense_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    bill_file: null
  });

  const [fileName, setFileName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchDropdowns();
      if (expense) {
        fetchExpenseDetails(expense.id);
      } else {
        resetForm();
      }
    }
  }, [isOpen, expense]);

  const resetForm = () => {
    setFormData({
      project_id: '',
      category_id: '',
      expense_by: '',
      expense_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: '',
      bill_file: null
    });
    setFileName('');
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
  };

  const fetchDropdowns = async () => {
    setIsLoading(true);
    try {
      const [projRes, catRes, empRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/expense-categories-dropdown'),
        api.get('/employees-dropdown')
      ]);

      setDropdowns({
        projects: projRes.data.data || [],
        categories: catRes.data.data || [],
        employees: empRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error('Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExpenseDetails = async (id) => {
    setIsFetching(true);
    try {
      const response = await api.get(`/expenses/${id}`);
      if (response.data.success) {
        const exp = response.data.data;
        setFormData({
          project_id: exp.project_id || '',
          category_id: exp.category_id || '',
          expense_by: exp.expense_by || '',
          expense_date: exp.expense_date || new Date().toISOString().split('T')[0],
          amount: exp.amount || '',
          description: exp.description || '',
          bill_file: null // Files aren't pre-filled for security
        });
        if (exp.bill_filename) setFileName(exp.bill_filename);
        if (exp.bill_filepath) {
          setPreviewUrl(getStorageUrl(exp.bill_filepath));
        }
      }
    } catch (error) {
      console.error('Error fetching expense details:', error);
      toast.error('Failed to load expense details');
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit');
        return;
      }

      // Cleanup old preview if it was a blob URL
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }

      setFormData(prev => ({ ...prev, bill_file: file }));
      setFileName(file.name);

      // Generate new preview
      const reader = new FileReader();
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file));
      } else if (file.type === 'application/pdf') {
        setPreviewUrl('pdf-placeholder'); // Special marker for PDF
      } else {
        setPreviewUrl('');
      }
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setFormData(prev => ({ ...prev, bill_file: null }));
    setFileName('');
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isImage = (url, filename) => {
    if (!url && !filename) return false;
    const name = filename || (url && url.split('/').pop());
    if (!name) return false;
    const ext = name.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id || !formData.expense_by || !formData.amount || !formData.expense_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          data.append(key, formData[key]);
        }
      });

      // For Laravel spoofing of PUT/PATCH when using FormData
      if (expense) {
        data.append('_method', 'PUT');
      }

      const response = expense
        ? await api.post(`/expenses/${expense.id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post('/expenses', data, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (response.data.success) {
        toast.success(response.data.message || 'Expense saved successfully');
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.message || 'Failed to save expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles['drawer-overlay']} ${isOpen ? styles['open'] : ''}`} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        <header className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{expense ? 'Edit Expense' : 'Record New Expense'}</h2>
            <p>{expense ? 'Update existing expense details' : 'Log a new business or project expense'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </header>

        <div className={styles['drawer-body']}>
          {isFetching ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
              <Loader2 size={40} className={styles['spinner']} color="#f97316" />
              <span style={{ color: '#64748b', fontWeight: 600 }}>Loading technical details...</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles['drawer-section']}>
                <h3 className={styles['section-title']}>
                  <Briefcase size={18} className={styles['section-icon']} />
                  Project & Classification
                </h3>
                <div className={styles['form-grid']}>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Associated Project <span style={{ color: 'red' }}>*</span></label>
                    <CustomSelect
                      placeholder="Select Project"
                      options={dropdowns.projects.map(p => ({ value: p.id, label: p.project_name }))}
                      value={formData.project_id ? { value: formData.project_id, label: dropdowns.projects.find(p => p.id === formData.project_id)?.project_name } : null}
                      onChange={(opt) => handleInputChange('project_id', opt ? opt.value : '')}
                      isClearable={true}
                    />
                  </div>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Expense Category <span style={{ color: 'red' }}>*</span></label>
                    <CustomSelect
                      placeholder="Select Category"
                      options={dropdowns.categories.map(c => ({ value: c.id, label: c.category_name }))}
                      value={formData.category_id ? { value: formData.category_id, label: dropdowns.categories.find(c => c.id === formData.category_id)?.category_name } : null}
                      onChange={(opt) => handleInputChange('category_id', opt ? opt.value : '')}
                    />
                  </div>
                </div>
              </div>

              <div className={styles['drawer-section']}>
                <h3 className={styles['section-title']}>
                  <Wallet size={18} className={styles['section-icon']} />
                  Payment Details
                </h3>
                <div className={styles['form-grid']}>
                  <div className={styles['form-group']}>
                    <label>Amount <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontWeight: 700 }}>₹</span>
                      <input
                        type="number"
                        placeholder="0.00"
                        style={{ paddingLeft: '32px' }}
                        value={formData.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles['form-group']}>
                    <label>Expense Date <span style={{ color: 'red' }}>*</span></label>
                    <CustomDatePicker
                      selected={formData.expense_date}
                      onChange={(date) => handleInputChange('expense_date', date ? date.toISOString().split('T')[0] : '')}
                    />
                  </div>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Expense Incurred By (Employee) <span style={{ color: 'red' }}>*</span></label>
                    <CustomSelect
                      placeholder="Select Employee"
                      options={dropdowns.employees.map(e => ({ value: e.id, label: `${e.employee_name} (${e.designation_name})` }))}
                      value={formData.expense_by ? { value: formData.expense_by, label: dropdowns.employees.find(e => e.id === formData.expense_by)?.employee_name || '' } : null}
                      onChange={(opt) => handleInputChange('expense_by', opt ? opt.value : '')}
                    />
                  </div>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Description</label>
                    <textarea
                      placeholder="What was this expense for?"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className={styles['drawer-section']}>
                <h3 className={styles['section-title']}>
                  <Upload size={18} className={styles['section-icon']} />
                  Supporting Document
                </h3>
                <div className={styles['file-upload-container']}>
                  <div
                    className={`${styles['file-upload-wrapper']} ${previewUrl ? styles['has-preview'] : ''}`}
                    onClick={() => !previewUrl && fileInputRef.current.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                    />

                    {previewUrl ? (
                      <div className={styles['preview-container']}>
                        {previewUrl === 'pdf-placeholder' || (!previewUrl.startsWith('blob:') && !isImage(previewUrl, fileName)) ? (
                          <div className={styles['pdf-preview']}>
                            <FileCheck size={48} color="#0369a1" />
                            <div className={styles['file-meta']}>
                              <span className={styles['file-name']}>{fileName}</span>
                              <span className={styles['file-type']}>PDF Document</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles['image-preview-wrapper']}>
                            <img src={previewUrl} alt="Preview" className={styles['image-preview']} />
                          </div>
                        )}
                        <div className={styles['preview-overlay']}>
                          <button type="button" className={styles['remove-file-btn']} onClick={removeFile}>
                            <X size={18} />
                            <span>Remove File</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles['file-info']}>
                        <Upload size={32} color="#94a3b8" />
                        <span className={styles['file-name']}>Upload Bill/Receipt</span>
                        <span className={styles['upload-text']}>Max size: 5MB (PDF, JPG, PNG)</span>
                      </div>
                    )}
                  </div>
                  {previewUrl && (
                    <button
                      type="button"
                      className={styles['change-file-link']}
                      onClick={() => fileInputRef.current.click()}
                    >
                      Change Document
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>

        <footer className={styles['drawer-footer']}>
          <button className={`${styles['footer-btn']} ${styles['cancel-btn']}`} onClick={onClose}>
            Cancel
          </button>
          <button
            className={`${styles['footer-btn']} ${styles['submit-btn']}`}
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? <Loader2 size={18} className={styles['spinner']} /> : <CheckCircle2 size={18} />}
            <span>{isSubmitting ? (expense ? 'Updating...' : 'Recording...') : (expense ? 'Update Expense' : 'Record Expense')}</span>
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ExpenseDrawer;
